import { tokenStore } from 'src/queries/tokenStore'
import { RootState } from 'src/redux/createRootReducer'

/**
 * Interop write-through: mirror the Redux auth token fields into the non-Redux
 * `tokenStore` on every change (the single bridge for auth-redux-elimination slice
 * 2). Redux remains the WRITE source; `tokenStore` is the transport READ source.
 *
 * In the browser, it runs an immediate sync so the hydrated initial `state.auth`
 * is captured before the first authed request is built, then subscribes for every
 * subsequent change. Returns the store's unsubscribe handle.
 *
 * Wired only on the browser entry (`src/client.tsx`). SSR does not subscribe a
 * per-request Redux store into this process-level mirror; `src/server.ts` seeds a
 * request-scoped token context directly via `runWithTokenContext` so concurrent
 * requests cannot share a bearer or leak subscriptions.
 *
 * This is the documented "single read of `state.auth.*` token fields" leave (the
 * intentional browser bridge).
 */
type SubscribableStore = {
  getState: () => RootState
  subscribe: (listener: () => void) => () => void
}

export const startTokenStoreMirror = (
  store: SubscribableStore,
): (() => void) => {
  const sync = (): void => {
    const { jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId } =
      store.getState().auth
    tokenStore.setTokens({
      jwt,
      refreshToken,
      cspJwt,
      cspRefreshToken,
      userSessionTokenId,
    })
  }

  sync()
  return store.subscribe(sync)
}
